import { ActivityMode, ContentType, PostMessageType } from '@/models/Session';
import { ActivityProgressUpdate } from '@/models/Activity';

import { computed, ref, watchEffect } from 'vue';
import { defineStore, storeToRefs } from 'pinia';
import { useRoute } from 'vue-router';

import { createMessage, EventBus, Message } from '@goodhabitz/learning-activity-communication';
import {
  GhccMessageType,
  registerGhccEvents as listenGhccEvents,
} from '@goodhabitz/ghcc-activity-communication';

import { GHRoutes } from '@/router/routes';

import { useSessionStore } from './useSessionStore';
import { useNavigationStore } from './useNavigationStore';
import { useGhccSessionStore } from './useGhccSessionStore';
import { useActivityProgressStore } from './useActivityProgressStore';

export const useGhccIframeStore = defineStore('ghccIframe', () => {
  const route = useRoute();

  const source = ref<string>();
  const eventBus = ref<EventBus>();

  const lastActivityId = ref<string>();

  const sessionStore = useSessionStore();
  const { contentType, activityMode } = storeToRefs(sessionStore);
  const { sendPostMessage } = sessionStore;

  const { redirectToContentPage } = useNavigationStore();

  const { setProgress, startSessionTime, stopSessionTime } = useActivityProgressStore();

  const ghccSessionStore = useGhccSessionStore();
  const { ssoUrl } = storeToRefs(ghccSessionStore);
  const { setPassportId, setLinkSpeed } = ghccSessionStore;

  const isReadyToSubscribe = computed(() => !source.value && ssoUrl.value);
  const isCourse = computed(
    () => route.name !== GHRoutes.LearningActivityPage && contentType.value === ContentType.course,
  );

  const onNewPassport = ({ payload }: Message) => {
    if (!payload?.SessionIdentifier) return;

    const { SessionIdentifier } = payload;

    setPassportId(SessionIdentifier);
  };

  const onLinkSpeed = ({ payload }: Message) => {
    if (!payload) return;

    const { AVG } = payload;

    setLinkSpeed(!AVG || AVG < 100 ? 100 : AVG);
  };

  const onInitializeActivity = ({ payload }: Message) => {
    if (!payload?.ActivityClientID) return;

    const { ActivityClientID } = payload;

    lastActivityId.value = ActivityClientID.replace('G_', '');

    if (isCourse.value) startSessionTime();
  };

  const onUpdateActivityStatus = ({ payload }: Message) => {
    if (!payload?.Status) return;

    const {
      Status: { ClientID, Progress, MaxProgress, ScaledScore },
    } = payload;

    const data: ActivityProgressUpdate = {
      courseCode: ClientID.replace('G_', ''),
      completionPercent: Math.round((Progress / MaxProgress) * 100),
      score: ScaledScore,
    };

    if (isCourse.value) {
      setProgress(data);
    } else {
      sendPostMessage({
        type: PostMessageType.updateActivityProgress,
        data,
      });
    }
  };

  const onTerminateActivity = () => {
    if (isCourse.value) {
      stopSessionTime();

      if (activityMode.value === ActivityMode.normal) {
        redirectToContentPage();
      }
    }
  };

  const subscribeToEvents = () => {
    if (eventBus.value) return;

    eventBus.value = new EventBus(window.location.origin);

    // TODO: add UpdateActivityStatus into ghcc-activity-communication package
    eventBus.value.on(GhccMessageType.newPassport, onNewPassport);
    eventBus.value.on(GhccMessageType.linkSpeed, onLinkSpeed);
    eventBus.value.on(GhccMessageType.initializeActivity, onInitializeActivity);
    eventBus.value.on('UpdateActivityStatus', onUpdateActivityStatus);
    eventBus.value.on(GhccMessageType.terminateActivity, onTerminateActivity);

    eventBus.value.connect(document.getElementById('ghcc-content') as HTMLIFrameElement);

    // TODO: Provide token when BFF is deployed
    listenGhccEvents(false, '');

    source.value = ssoUrl.value;
  };

  const sendStartActivityMessage = (courseCode: string) => {
    if (!eventBus.value?.isConnected) return;

    const startActivityMessage = createMessage<{
      type: GhccMessageType.startActivity;
      payload: { id: string | undefined };
    }>(GhccMessageType.startActivity, {
      id: courseCode,
    });

    eventBus.value.send(startActivityMessage);
  };

  watchEffect(() => {
    if (isReadyToSubscribe.value) {
      subscribeToEvents();
    }
  });

  return {
    source,
    eventBus,
    lastActivityId,
    subscribeToEvents,
    sendStartActivityMessage,
  };
});
